import { isSendShortcut } from '../lib/composer_enter.js'
/**
 * SkillAutocomplete hook for /skill command search.
 * Based on mention_autocomplete.js pattern.
 *
 * Responsibilities:
 * - Detect / at word boundaries
 * - Calculate cursor position for dropdown
 * - Handle keyboard navigation
 * - Set active skill on selection
 */
export default {
  mounted () {
    // The hook is attached to the parent div, find the contenteditable
    this.editor = this.el.querySelector('[contenteditable="true"]')
    if (!this.editor) {
      console.warn('[SkillAutocomplete] Contenteditable not found')
      return
    }

    // State
    this.skills = []
    this.isAutocompleteVisible = false

    // Bind event listeners
    this.handleInput = this.handleInput.bind(this)
    this.handleKeydown = this.handleKeydown.bind(this)

    this.editor.addEventListener('input', this.handleInput)
    // Use capture phase to intercept keydown before form submission
    this.editor.addEventListener('keydown', this.handleKeydown, true)

    // Listen for skills list from LiveView
    this.handleEvent('skills_list_cached', (data) => {
      this.skills = data.skills || []

      // Re-filter with cached skills
      if (this.isAutocompleteVisible) {
        this.filterAndUpdate()
      }
    })

    // Listen for skill selection confirmation from LiveView
    this.handleEvent('skill_activated', () => {
      this.removeSlashCommand()
      this.hideAutocomplete()
    })

    // Listen for scroll into view events from server
    this.handleEvent('scroll_skill_into_view', (data) => {
      const selectedEl = document.getElementById(`skill-autocomplete-item-${data.index}`)
      if (selectedEl) {
        selectedEl.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
      }
    })
  },

  /**
   * Handles input changes in contenteditable.
   * Detects /skill commands at word boundaries.
   */
  handleInput () {
    // Get text content before cursor
    const beforeCursor = this.getTextBeforeCursor()

    if (beforeCursor === null) {
      return
    }

    // Match / at word boundary followed by any alphanumeric/space/dash chars
    const match = beforeCursor.match(/(?:^|\s|\n)\/([\w\s-]*)$/)

    if (match) {
      // Found / command
      const query = match[1]

      // Show autocomplete
      if (!this.isAutocompleteVisible) {
        this.isAutocompleteVisible = true

        // Calculate dropdown position
        const position = this.calculateCursorPosition()

        // Push event to LiveView to show autocomplete
        this.pushEvent('show_skill_autocomplete', {
          query: query,
          position: position
        })
      } else {
        // Already visible, just update filter
        this.filterAndUpdate(query)
      }
    } else {
      // No / command detected
      if (this.isAutocompleteVisible) {
        this.hideAutocomplete()
      }
    }
  },

  /**
   * Gets text content before the cursor position in contenteditable.
   * Replaces mention and skill spans with empty string to avoid false detection.
   */
  getTextBeforeCursor () {
    const selection = window.getSelection()
    if (!selection.rangeCount) {return null}

    const range = selection.getRangeAt(0)
    const preCaretRange = range.cloneRange()
    preCaretRange.selectNodeContents(this.editor)
    preCaretRange.setEnd(range.endContainer, range.endOffset)

    // Extract text but skip mention/skill spans
    const fragment = preCaretRange.cloneContents()
    let text = ''

    const extractText = (node) => {
      if (node.nodeType === Node.TEXT_NODE) {
        text += node.textContent
      } else if (node.nodeType === Node.ELEMENT_NODE) {
        // Skip mention and skill spans
        if (node.classList.contains('mention') || node.classList.contains('skill-indicator')) {
          return
        }
        // Process children
        for (const child of node.childNodes) {
          extractText(child)
        }
      }
    }

    for (const node of fragment.childNodes) {
      extractText(node)
    }

    return text
  },

  /**
   * Handles keyboard events for navigation.
   */
  handleKeydown (e) {
    // Only intercept if autocomplete is visible
    if (!this.isAutocompleteVisible) {return}

    // Skip while an IME (e.g. Japanese/Chinese/Korean) is composing.
    if (e.isComposing || e.keyCode === 229) {return}

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault()
        this.pushEvent('skill_autocomplete_navigate', { direction: 'down' })
        break

      case 'ArrowUp':
        e.preventDefault()
        this.pushEvent('skill_autocomplete_navigate', { direction: 'up' })
        break

      case 'Enter':
        // Only if autocomplete is visible and not shift+enter (newline). Ctrl/Cmd+Enter
        // is the always-send shortcut, so it passes through to the composer.
        if (!e.shiftKey && !isSendShortcut(e)) {
          e.preventDefault()
          e.stopPropagation()
          this.pushEvent('skill_autocomplete_select', {})
        }
        break

      case 'Escape':
        e.preventDefault()
        this.hideAutocomplete()
        break

      case 'Tab':
        // Allow tab to select as well
        e.preventDefault()
        e.stopPropagation()
        this.pushEvent('skill_autocomplete_select', {})
        break
    }
  },

  /**
   * Filters skills based on query and pushes to LiveView.
   */
  filterAndUpdate (query) {
    if (query === undefined) {
      // Extract query from current contenteditable
      const beforeCursor = this.getTextBeforeCursor()
      const match = beforeCursor ? beforeCursor.match(/(?:^|\s|\n)\/([\w\s-]*)$/) : null
      query = match ? match[1] : ''
    }

    // Push to LiveView
    this.pushEvent('update_skill_autocomplete', {
      query: query
    })
  },

  /**
   * Calculate position for dropdown (above the editor).
   */
  calculateCursorPosition () {
    const editor = this.editor
    const editorRect = editor.getBoundingClientRect()

    const dropdownMaxWidth = 400
    const padding = 20

    // Calculate x position - align to left of editor, but stay in viewport
    let x = editorRect.left
    if (x + dropdownMaxWidth > window.innerWidth - padding) {
      x = Math.max(padding, window.innerWidth - dropdownMaxWidth - padding)
    }

    // Position at the top of the editor
    // CSS will use transform: translateY(-100%) to position bottom at this point
    const y = editorRect.top

    return { x, y }
  },

  /**
   * Removes the /command text from the editor after skill selection.
   */
  removeSlashCommand () {
    // Get current text content from editor
    const currentText = this.editor.textContent

    // Find the / command in the current text
    const match = currentText.match(/(?:^|\s|\n)\/([\w\s-]*)$/)

    if (!match) {
      return
    }

    // Find the position of the / in the editor
    const slashIndex = currentText.lastIndexOf(match[0])
    const actualSlashIndex = slashIndex + (match[0].startsWith(' ') || match[0].startsWith('\n') ? 1 : 0)

    // Create a range to select and delete the / and query text
    const selection = window.getSelection()
    const range = document.createRange()

    // Find the text node containing the /
    let textNode = null
    let nodeOffset = 0
    let currentOffset = 0

    // Walk through text nodes to find the right position
    const walkTextNodes = (node) => {
      if (node.nodeType === Node.TEXT_NODE) {
        const nodeLength = node.textContent.length
        if (currentOffset + nodeLength > actualSlashIndex && !textNode) {
          textNode = node
          nodeOffset = actualSlashIndex - currentOffset
        }
        currentOffset += nodeLength
      } else if (node.nodeType === Node.ELEMENT_NODE) {
        // Skip mention and skill spans
        if (node.classList && (node.classList.contains('mention') || node.classList.contains('skill-indicator'))) {
          return
        }
        for (const child of node.childNodes) {
          walkTextNodes(child)
        }
      }
    }

    walkTextNodes(this.editor)

    if (!textNode) {
      console.error('[SkillAutocomplete] Could not find text node with /')
      return
    }

    // Delete the / and query text
    try {
      range.setStart(textNode, nodeOffset)
      range.setEnd(textNode, textNode.textContent.length)
      range.deleteContents()

      // Move cursor to the position
      range.collapse(true)
      selection.removeAllRanges()
      selection.addRange(range)

      // Trigger input event to sync with hidden input
      this.editor.dispatchEvent(new Event('input', { bubbles: true }))
    } catch (err) {
      console.error('[SkillAutocomplete] Error removing slash command:', err)
    }
  },

  /**
   * Hides the autocomplete dropdown.
   */
  hideAutocomplete () {
    if (this.isAutocompleteVisible) {
      this.isAutocompleteVisible = false
      this.pushEvent('hide_skill_autocomplete', {})
    }
  },

  destroyed () {
    if (this.editor) {
      this.editor.removeEventListener('input', this.handleInput)
      this.editor.removeEventListener('keydown', this.handleKeydown, true)
    }
  }
}
